import { Inertia } from '@inertiajs/inertia';

/* Page views and contacts for the public site.

   The tags are added in app.blade.php and only in production, so this file
   does nothing when they are absent. Inertia swaps the page in place, so a
   page view is sent from here. The first Meta PageView is the one in the
   base snippet. Later ones are sent here. ChatGPT Ads does not send its own
   page view from the snippet, so page_viewed is sent here, including the
   first. A visit that reports the same address twice is sent once. */

const OFFER_EXACT = {
    '/web-design': 'service',
    '/digital-marketing': 'service',
    '/mobile-application': 'service',
    '/ai-solutions': 'service',
    '/brand-presence': 'service',
    '/promotions': 'promotion',
};

let lastUrl = null;
let chatSent = false;

function live() {
    return typeof window.gtag === 'function'
        || typeof window.fbq === 'function'
        || typeof window.oaiq === 'function';
}

function eventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return 'evt-' + Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function measure(name, data, options) {
    if (typeof window.oaiq !== 'function') {
        return;
    }

    if (options) {
        window.oaiq('measure', name, data, options);

        return;
    }

    window.oaiq('measure', name, data);
}

function pageContent(pathname, title, contentType) {
    const item = {
        id: pathname || '/',
        content_type: contentType,
    };
    const name = String(title || '').trim();

    if (name) {
        item.name = name.slice(0, 200);
    }

    return {
        type: 'contents',
        contents: [item],
    };
}

function normalizeName(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g, '');
}

function normalizePhone(value) {
    let digits = String(value || '').replace(/[\s().\-]/g, '');

    if (digits.charAt(0) === '+') {
        digits = digits.slice(1);
    }

    digits = digits.replace(/^0+/, '');

    if (!/^[0-9]{8,15}$/.test(digits)) {
        return '';
    }

    return digits;
}

function sha256Hex(value) {
    if (!value || !window.crypto || !window.crypto.subtle || typeof TextEncoder !== 'function') {
        return Promise.resolve('');
    }

    return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)).then(function (buffer) {
        const view = new Uint8Array(buffer);
        let hex = '';

        for (let i = 0; i < view.length; i++) {
            hex += view[i].toString(16).padStart(2, '0');
        }

        return hex;
    }).catch(function () {
        return '';
    });
}

function measureLead(id, email, name, phone) {
    if (typeof window.oaiq !== 'function') {
        return;
    }

    const emailNorm = String(email || '').trim().toLowerCase();
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    const first = normalizeName(parts[0] || '');
    const last = normalizeName(parts.slice(1).join(' '));
    const phoneNorm = normalizePhone(phone);

    Promise.all([
        sha256Hex(emailNorm),
        sha256Hex(first),
        sha256Hex(last),
        sha256Hex(phoneNorm),
    ]).then(function (hashes) {
        const user = {};

        if (hashes[0]) {
            user.email_sha256 = hashes[0];
        }

        if (hashes[1]) {
            user.first_name_sha256 = hashes[1];
        }

        if (hashes[2]) {
            user.last_name_sha256 = hashes[2];
        }

        if (hashes[3]) {
            user.phone_number_sha256 = hashes[3];
        }

        const init = {};

        if (window.VEXIMUS_OPENAI_PIXEL_ID) {
            init.pixelId = window.VEXIMUS_OPENAI_PIXEL_ID;
        }

        if (user.email_sha256 || user.first_name_sha256 || user.last_name_sha256 || user.phone_number_sha256) {
            init.user = user;
        }

        if (init.pixelId || init.user) {
            window.oaiq('init', init);
        }

        window.oaiq('measure', 'lead_created', { type: 'customer_action' }, { event_id: id });
    });
}

function absolute(href) {
    const url = new URL(href, window.location.origin);

    url.hash = '';

    return url.href;
}

function skipped(pathname) {
    return pathname === '/admin'
        || pathname.indexOf('/admin/') === 0
        || pathname.indexOf('/automation/preview') === 0;
}

function offerPath(pathname) {
    if (pathname === '/ms' || pathname === '/zh') {
        return '/';
    }

    if (pathname.indexOf('/ms/') === 0 || pathname.indexOf('/zh/') === 0) {
        return pathname.slice(3);
    }

    return pathname;
}

function offerType(pathname) {
    const path = offerPath(pathname);

    if (OFFER_EXACT[path]) {
        return OFFER_EXACT[path];
    }

    if (path.indexOf('/promotions/') === 0) {
        return 'promotion';
    }

    if (path.indexOf('/portfolios/') === 0 && path !== '/portfolios/') {
        return 'portfolio';
    }

    return null;
}

function titleOf(page) {
    const seo = page && page.props && page.props.seo;

    if (seo && seo.title) {
        return seo.title;
    }

    return document.title;
}

function csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]');

    return meta ? meta.getAttribute('content') || '' : '';
}

function pageOf(event) {
    const detail = event && event.detail;

    if (!detail) {
        return null;
    }

    return detail.page || null;
}

/**
 * The first call uses the real landing address, query included. Later calls
 * use the Inertia address and the previous address as the referrer.
 */
function track(page, href, pixelPageView) {
    let url;

    try {
        url = absolute(href);
    } catch (error) {
        return;
    }

    if (url === lastUrl) {
        return;
    }

    const parsed = new URL(url);

    if (skipped(parsed.pathname)) {
        lastUrl = url;

        return;
    }

    const referrer = lastUrl || document.referrer || undefined;
    const title = titleOf(page);

    lastUrl = url;

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
            page_location: url,
            page_title: title,
            page_referrer: referrer,
        });
    }

    if (pixelPageView && typeof window.fbq === 'function') {
        window.fbq('track', 'PageView');
    }

    measure('page_viewed', pageContent(parsed.pathname, title, 'page'));

    const type = offerType(parsed.pathname);

    if (!type) {
        return;
    }

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'view_content', {
            content_type: type,
            content_name: title,
            page_location: url,
            page_title: title,
        });
    }

    if (typeof window.fbq === 'function') {
        window.fbq('track', 'ViewContent', {
            content_type: type,
            content_name: title,
        });
    }

    measure('contents_viewed', pageContent(parsed.pathname, title, type));
}

function methodOf(href) {
    if (!href) {
        return null;
    }

    if (href.indexOf('wa.me') !== -1) {
        return 'whatsapp';
    }

    if (href.indexOf('tel:') === 0) {
        return 'phone';
    }

    if (href.indexOf('mailto:') === 0) {
        return 'email';
    }

    return null;
}

function beacon(id, method) {
    if (!navigator.sendBeacon || typeof route !== 'function') {
        return;
    }

    const body = new URLSearchParams();

    body.set('_token', csrf());
    body.set('event_id', id);
    body.set('event_name', 'Contact');
    body.set('method', method);
    body.set('event_source_url', window.location.href);

    const payload = new Blob([body.toString()], { type: 'application/x-www-form-urlencoded' });

    navigator.sendBeacon(route('analytics.contact'), payload);
}

function contact(method) {
    if (!live()) {
        return;
    }

    const id = eventId();

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'contact', { method: method });
    }

    if (typeof window.fbq === 'function') {
        window.fbq('track', 'Contact', { method: method }, { eventID: id });
    }

    measure('lead_created', { type: 'customer_action' }, { event_id: id });
    beacon(id, method);
}

function onClick(event) {
    if (event.defaultPrevented || event.button !== 0) {
        return;
    }

    const node = event.target;

    if (!node || !node.closest) {
        return;
    }

    const link = node.closest('a[href]');

    if (!link) {
        return;
    }

    const method = methodOf(link.getAttribute('href'));

    if (method) {
        contact(method);
    }
}

export function bootAnalytics(initialPage) {
    if (!live()) {
        return;
    }

    track(initialPage, window.location.href, false);

    const visit = (event) => {
        const page = pageOf(event);

        if (page && page.url) {
            track(page, page.url, true);
        }
    };

    Inertia.on('success', visit);
    Inertia.on('navigate', visit);
    document.addEventListener('click', onClick);
}

/**
 * The enquiry form, after the server has accepted it. The id is the one
 * posted with the form. Name, email and phone are match keys. Meta hashes
 * them itself. ChatGPT Ads receives the hashes.
 */
export function trackLead(id, email, name, phone) {
    if (!live() || !id) {
        return;
    }

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', { method: 'form' });
    }

    if (typeof window.fbq === 'function') {
        const first = String(name || '').trim().split(/\s+/)[0] || '';
        const user = {};

        if (email) {
            user.em = String(email).trim().toLowerCase();
        }

        if (first) {
            user.fn = first.toLowerCase();
        }

        if (window.VEXIMUS_PIXEL_ID && (user.em || user.fn)) {
            window.fbq('init', window.VEXIMUS_PIXEL_ID, user);
        }

        window.fbq('track', 'Lead', { method: 'form' }, { eventID: id });
    }

    measureLead(id, email, name, phone);
}

/**
 * The first accepted chat message in this conversation.
 */
export function trackChat() {
    if (chatSent) {
        return;
    }

    chatSent = true;
    contact('chat');
}
